/**
 * RelationshipEgoGraph — generalizes MiniEgoGraph's visual language
 * (centered focal node, neighbours arrayed left/right, SVG, useSvgPanZoom)
 * to relationshipsFor() edges across all 9 ObjectKinds. Reuses MiniEgoGraph's
 * mini-ego-* CSS classes directly — same skeleton, different data source;
 * per-neighbour colour/icon comes from KIND_META via inline style. There is
 * no OUT/IN direction concept here (relationshipsFor flattens forward +
 * reverse edges into one undirected list) — neighbours are simply
 * alternated left/right by array index for a visually balanced split.
 * MiniEgoGraph itself is untouched; it stays the field-level,
 * Reference-specific view for entity-to-entity refs.
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 3
 */
import { useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { relationshipsFor } from '../state/selectors.js';
import { KIND_META, type ObjectKind, type RelationEdge } from '../state/objectKinds.js';
import { Icon } from '../components/Icon.js';
import { useSvgPanZoom } from '../state/useSvgPanZoom.js';

export interface RelationshipEgoGraphProps {
  manifest: AtlasManifest;
  kind: ObjectKind;
  id: string;
  onNavigate: (kind: ObjectKind, id: string) => void;
}

const MAX_PER_SIDE = 5;
const W = 600;
const H = 220;

function splitSides(edges: RelationEdge[]): { left: RelationEdge[]; right: RelationEdge[] } {
  const left: RelationEdge[] = [];
  const right: RelationEdge[] = [];
  edges.forEach((edge, i) => (i % 2 === 0 ? left : right).push(edge));
  return { left, right };
}

function placeColumn(n: number, startY = 30, endY = H - 30, cy = H / 2): number[] {
  if (n === 0) return [];
  if (n === 1) return [cy];
  const step = (endY - startY) / (n - 1);
  return Array.from({ length: n }, (_, i) => startY + i * step);
}

function abbreviate(s: string, max = 8): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

export function RelationshipEgoGraph({
  manifest,
  kind,
  id,
  onNavigate,
}: RelationshipEgoGraphProps): React.JSX.Element | null {
  const edges = relationshipsFor(manifest, kind, id);
  const panZoom = useSvgPanZoom();
  const [tooltip, setTooltip] = useState<{ x: number; y: number; title: string; lines: string[] } | null>(null);
  const [leftVisibleCount, setLeftVisibleCount] = useState(MAX_PER_SIDE);
  const [rightVisibleCount, setRightVisibleCount] = useState(MAX_PER_SIDE);

  if (edges.length === 0) return null;

  const { left: leftAll, right: rightAll } = splitSides(edges);
  const left = leftAll.slice(0, leftVisibleCount);
  const right = rightAll.slice(0, rightVisibleCount);
  const leftOverflow = Math.max(0, leftAll.length - leftVisibleCount);
  const rightOverflow = Math.max(0, rightAll.length - rightVisibleCount);

  const cx = W / 2;
  const cy = H / 2;
  const sideX = { left: 80, right: W - 80 };
  const leftYs = placeColumn(left.length);
  const rightYs = placeColumn(right.length);

  const renderSide = (items: RelationEdge[], ys: number[], x: number, prefix: string): React.JSX.Element[] =>
    items.map((edge, i) => {
      const meta = KIND_META[edge.kind];
      const y = ys[i] ?? cy;
      return (
        <g key={`${prefix}-${edge.kind}-${edge.id}`}>
          <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--graph-edge, rgba(248,250,252,0.4))" strokeWidth="1.2" />
          <text x={(cx + x) / 2} y={(cy + y) / 2 - 4} textAnchor="middle" fontSize="8" fontFamily="var(--ff-mono)" fill="var(--text2)">
            {edge.relation}
          </text>
          <g
            className="mini-ego-bubble"
            tabIndex={0}
            role="button"
            aria-label={`neighbour ${edge.id} (${meta.label}) via ${edge.relation}`}
            onClick={() => onNavigate(edge.kind, edge.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate(edge.kind, edge.id);
              }
            }}
            onMouseEnter={() => setTooltip({ x, y, title: edge.id, lines: [`${meta.label} · ${edge.relation}`] })}
            onMouseLeave={() => setTooltip(null)}
          >
            <circle cx={x} cy={y} r="17" style={{ fill: 'var(--s3)', stroke: meta.color }} />
            <text
              className="material-symbols-rounded"
              x={x}
              y={y - 3}
              textAnchor="middle"
              fontSize="10"
              style={{ fill: meta.color }}
            >
              {meta.icon}
            </text>
            <text x={x} y={y + 11} textAnchor="middle" fontSize="7" fontFamily="var(--ff-mono)" fontWeight="700">
              {abbreviate(edge.id, 8)}
            </text>
          </g>
        </g>
      );
    });

  return (
    <div className="mini-ego-graph">
      <div className="mini-ego-canvas">
        <div className="mini-ego-zoom-controls">
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.zoomIn} aria-label="Zoom in">
            <Icon name="zoom_in" size="sm" />
          </button>
          <span className="mini-ego-zoom-level">{panZoom.scalePercent}%</span>
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.zoomOut} aria-label="Zoom out">
            <Icon name="zoom_out" size="sm" />
          </button>
          <button type="button" className="mini-ego-zoom-btn" onClick={panZoom.reset} aria-label="Reset zoom">
            <Icon name="restart_alt" size="sm" />
          </button>
        </div>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-label={`${id} — relationship graph`}
          {...panZoom.svgProps}
        >
          <g transform={panZoom.transform}>
            {renderSide(left, leftYs, sideX.left, 'left')}
            {renderSide(right, rightYs, sideX.right, 'right')}
            <g aria-label={`focal ${kind} ${id}`}>
              <circle cx={cx} cy={cy} r="28" className="mini-ego-focal-glow" />
              <circle cx={cx} cy={cy} r="18" className="mini-ego-focal" style={{ stroke: KIND_META[kind].color }} />
              <text
                x={cx}
                y={cy + 4}
                textAnchor="middle"
                fontSize="11"
                fontFamily="var(--ff-mono)"
                fontWeight="800"
                fill="var(--text-on-brand, #0E0D13)"
              >
                {abbreviate(id, 10)}
              </text>
            </g>
          </g>
        </svg>
        {tooltip && (
          <div className="mini-ego-tooltip" style={{ left: `${(tooltip.x / W) * 100}%`, top: `${(tooltip.y / H) * 100}%` }}>
            <div className="mini-ego-tooltip-title">{tooltip.title}</div>
            {tooltip.lines.map((line, i) => (
              <div key={i} className="mini-ego-tooltip-line">{line}</div>
            ))}
          </div>
        )}
      </div>
      {(leftOverflow > 0 || rightOverflow > 0) && (
        <div className="mini-ego-overflow">
          {leftOverflow > 0 && (
            <button type="button" className="mini-ego-overflow-chip" onClick={() => setLeftVisibleCount(leftAll.length)}>
              +{leftOverflow} more
            </button>
          )}
          {rightOverflow > 0 && (
            <button type="button" className="mini-ego-overflow-chip" onClick={() => setRightVisibleCount(rightAll.length)}>
              +{rightOverflow} more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
